import { useQuery } from "@tanstack/react-query";
import { AjaxResponse } from "../../_core/api/types";
import { doesHubUserExist, getUserpoolPasswordPolicy } from "../api";
import { PasswordPolicy, UserAuthSession } from "../types";
import { AuthSession, fetchAuthSession } from "aws-amplify/auth";

const transformAuthSession = (session: AuthSession): UserAuthSession | undefined => {
  if (!session.tokens?.accessToken?.toString() || !session.tokens?.idToken?.toString()) {
    return undefined;
  }

  const accessToken = session.tokens.accessToken.toString();
  const idToken = session.tokens.idToken.toString();
  
  const idTokenPayload = session.tokens.idToken.payload;
  const userSub = idTokenPayload?.sub ?? "";
  
  const userProfile = {
    firstName: (idTokenPayload?.given_name as string) ?? null,
    lastName: (idTokenPayload?.family_name as string) ?? null,
    username: (idTokenPayload?.["cognito:username"] as string) ?? (idTokenPayload?.username as string) ?? null,
    email: (idTokenPayload?.email as string) ?? null
  };

  return {
    accessToken,
    idToken,
    userSub,
    userProfile
  };
};

export const useAuthSession = (options?: any) => {
  return useQuery<AuthSession, Error, UserAuthSession | undefined>({
    queryKey: ["authSession"],
    queryFn: () => fetchAuthSession(),
    staleTime: 0, // Always considered stale - fetch on every use
    gcTime: 0, // Don't cache at all - Amplify caches tokens on its own
    select: transformAuthSession,
    ...options
  });
};

export function useHubUserExist(options?: any) {
  return useQuery<boolean, AjaxResponse>({
    queryKey: ["hubUserExist"],
    queryFn: doesHubUserExist,
    ...options
  });
}

export const useCognitoUserpoolPolicy = (options?: any) => {
  const userPoolId = extractUserPoolId(window.authConfig.tokenIssuerUrl);

  return useQuery<PasswordPolicy, AjaxResponse>({
    queryKey: ["cognitoUserpoolPolicy", userPoolId],
    queryFn: () => getUserpoolPasswordPolicy(userPoolId),
    enabled: Boolean(userPoolId),
    ...options
  });
};

export const extractUserPoolId = (url: string) => {
  const parts = url.split("/");

  return parts[parts.length - 1];
};